import React, { useState } from 'react';
import { useQueueStore } from '../../stores/queueStore';
import { ArrowPathIcon, PlayIcon, EyeIcon } from '@heroicons/react/24/outline';

interface RotationControlProps {
  className?: string;
  onRotationComplete?: () => void;
}

const RotationControl: React.FC<RotationControlProps> = ({ 
  className = '',
  onRotationComplete 
}) => {
  const { 
    queue, 
    currentPlayers, 
    restingPlayers, 
    isLoading, 
    error,
    rotateQueue, 
    canStartMatch,
    getRotationPreview,
    clearError
  } = useQueueStore();

  const [showPreview, setShowPreview] = useState(false);
  const [isRotating, setIsRotating] = useState(false);

  const handleRotateQueue = async () => {
    if (!queue || isLoading) return;

    setIsRotating(true);
    clearError();

    try {
      await rotateQueue();
      onRotationComplete?.();
    } catch (err) {
      console.error('轮换失败:', err);
    } finally {
      setIsRotating(false);
    }
  };

  const handleStartMatch = () => {
    if (canStartMatch()) {
      // 这里可以触发开始比赛的逻辑
      // 比如导航到配对页面或显示配对对话框
      console.log('开始比赛，当前参赛者:', currentPlayers);
    }
  };

  const rotationPreview = queue ? getRotationPreview(3) : [];

  if (!queue) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <div className="text-center text-gray-500">
          <p className="text-lg mb-2">队列未初始化</p>
          <p className="text-sm">无法进行轮换操作</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <h2 className="text-xl font-bold text-gray-800 mb-6">轮换控制</h2>

      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="text-red-600 text-sm">{error}</div>
            <button
              onClick={clearError}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* 当前状态 */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">当前参赛者</h3>
            <div className="space-y-1">
              {currentPlayers.map((player, index) => (
                <div key={player.id} className="text-sm text-gray-600">
                  {index + 1}. {player.name} (#{player.number})
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">休息中</h3>
            <div className="space-y-1">
              {restingPlayers.length > 0 ? (
                restingPlayers.map((player, index) => (
                  <div key={player.id} className="text-sm text-gray-600">
                    {player.name} (#{player.number})
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-400">无人休息</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 控制按钮 */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <button
          onClick={handleRotateQueue}
          disabled={isLoading || isRotating || queue.players.length < 2}
          className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          <ArrowPathIcon className={`w-5 h-5 ${isRotating ? 'animate-spin' : ''}`} />
          <span>{isRotating ? '轮换中...' : '开始新一轮'}</span>
        </button>

        <button
          onClick={handleStartMatch}
          disabled={!canStartMatch() || isLoading}
          className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          <PlayIcon className="w-5 h-5" />
          <span>开始比赛</span>
        </button>

        <button
          onClick={() => setShowPreview(!showPreview)}
          className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          <EyeIcon className="w-5 h-5" />
          <span>预览</span>
        </button>
      </div>

      {/* 轮换预览 */}
      {showPreview && (
        <div className="border-t pt-6">
          <h3 className="font-semibold text-gray-700 mb-4">轮换预览</h3>
          <div className="space-y-3">
            {rotationPreview.map((preview, index) => (
              <div
                key={preview.round}
                className={`p-3 rounded-lg border ${
                  index === 0 
                    ? 'bg-blue-50 border-blue-200' 
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-700">
                    第 {preview.round} 轮
                    {index === 0 && (
                      <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        下一轮
                      </span>
                    )}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-green-700 font-medium">参赛: </span>
                    <span className="text-gray-600">
                      {preview.players.map(p => p.name).join(', ')}
                    </span>
                  </div>
                  
                  <div>
                    <span className="text-orange-700 font-medium">休息: </span>
                    <span className="text-gray-600">
                      {preview.resting.length > 0 
                        ? preview.resting.map(p => p.name).join(', ')
                        : '无'
                      }
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 操作提示 */}
      <div className="text-xs text-gray-500 mt-4">
        <div className="space-y-1">
          <div>• 点击"开始新一轮"将第一个人移到队尾</div>
          <div>• 需要至少4个人才能开始比赛</div>
          <div>• 预览功能可以查看未来几轮的轮换情况</div>
        </div>
      </div>
    </div>
  );
};

export default RotationControl;